import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getCommissionProgressByAgentId } from '../../api/commissionProgressApi';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";
const TYPE_COLORS = { PSC: '#1A8CFF', DOC: '#10B981', TOC: '#F59E0B' };

const DATE_PRESETS = [
  { label: 'All Time', start: () => null, end: () => null },
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
];

const totalPaidOf = (cd) => (cd?.firstPayment || 0) + (cd?.secondPayment || 0)
  + (cd?.repayment || 0) + (cd?.repayment2 || 0) + (cd?.repayment3 || 0) + (cd?.repayment4 || 0) + (cd?.repayment5 || 0);

export default function CommissionAgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState('All Time');

  const fetchData = useCallback(async () => {
    try {
      const res = await getCommissionProgressByAgentId(agentId);
      // Only commission on sales that actually count: Active + Dispute Pricing. A
      // disputed sale is still a real case — only its pricing is under review — while
      // Pending has not been confirmed and Archived is out of the book entirely.
      //
      // isArchived is checked SEPARATELY from status: 67 rows sit on sales whose status
      // is still "Active" but which have been archived, so filtering on status alone
      // would let them through. Unfiltered, this page overstated by RM764,111 across
      // 485 rows. Same rule as the Sales Report. (user 2026-08-26)
      setRecords((res.data || []).filter(r => {
        const sd = r.salesDetailsForCommissionProgress;
        if (!sd) return true;                       // no sale detail → leave it visible
        if (sd.isArchived) return false;
        return sd.status !== 'Pending' && sd.status !== 'Refunded' && sd.status !== 'Archived';
      }));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  const filtered = useMemo(() => {
    if (!startDate && !endDate) return records;
    return records.filter(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return false;
      const d = dayjs(dep);
      if (startDate && d.isBefore(startDate, 'day')) return false;
      if (endDate && d.isAfter(endDate, 'day')) return false;
      return true;
    });
  }, [records, startDate, endDate]);

  const agentName = useMemo(() => {
    const r = records.find(x => x.commissionProgressDto?.paymentTo);
    return r?.commissionProgressDto?.paymentToName || `Agent #${agentId}`;
  }, [records, agentId]);

  const sumByType = (type) => filtered
    .filter(r => r.commissionProgressDto?.commPayType === type)
    .reduce((s, r) => s + (r.commissionProgressDto?.commPay || 0), 0);

  const psc = sumByType('PSC');
  const doc = sumByType('DOC');
  const toc = sumByType('TOC');
  const total = psc + doc + toc;
  const paid = useMemo(() => filtered.reduce((s, r) => s + totalPaidOf(r.commissionProgressDto), 0), [filtered]);
  const outstanding = Math.max(0, total - paid);

  const byType = [
    { name: 'PSC', value: psc },
    { name: 'DOC', value: doc },
    { name: 'TOC', value: toc },
  ].filter(x => x.value > 0);

  const byMonth = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return;
      const key = dayjs(dep).format('MMM YYYY');
      const t = r.commissionProgressDto?.commPayType;
      if (!map[key]) map[key] = { name: key, PSC: 0, DOC: 0, TOC: 0 };
      if (t && map[key][t] !== undefined) map[key][t] += (r.commissionProgressDto?.commPay || 0);
    });
    return Object.values(map).sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [filtered]);

  const recent = useMemo(() =>
    [...filtered].sort((a, b) => {
      const da = a.salesDetailsForCommissionProgress?.depositOn ? new Date(a.salesDetailsForCommissionProgress.depositOn).getTime() : 0;
      const db = b.salesDetailsForCommissionProgress?.depositOn ? new Date(b.salesDetailsForCommissionProgress.depositOn).getTime() : 0;
      return db - da;
    }).slice(0, 30), [filtered]);

  const cards = [
    { label: 'PSC', value: handlePriceWithRM(psc), color: '#1A8CFF' },
    { label: 'DOC', value: handlePriceWithRM(doc), color: '#10B981' },
    { label: 'TOC', value: handlePriceWithRM(toc), color: '#F59E0B' },
    { label: 'Total', value: handlePriceWithRM(total), color: '#0F172A' },
    { label: 'Paid', value: handlePriceWithRM(paid), color: '#10B981' },
    { label: 'Outstanding', value: handlePriceWithRM(outstanding), color: '#EF4444' },
  ];

  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ fontFamily: FONT, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
          Back to Commission Report
        </Button>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.8rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>{agentName}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>Commission breakdown · {records.length} total records</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
        {cards.map(c => (
          <Card key={c.label} sx={{ borderLeft: `4px solid ${c.color}`, borderRadius: '14px' }}>
            <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{c.label}</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: c.color, mt: 0.5, fontFamily: FONT }}>{c.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Monthly Breakdown</Typography>
            {byMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip formatter={v => handlePriceWithRM(v)} />
                  <Legend />
                  <Bar dataKey="PSC" stackId="a" fill={TYPE_COLORS.PSC} />
                  <Bar dataKey="DOC" stackId="a" fill={TYPE_COLORS.DOC} />
                  <Bar dataKey="TOC" stackId="a" fill={TYPE_COLORS.TOC} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Type Split</Typography>
            {byType.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={byType} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value"
                    label={({ name, value }) => `${name}: ${handlePriceWithRM(value)}`} labelLine={false}>
                    {byType.map(entry => <Cell key={entry.name} fill={TYPE_COLORS[entry.name]} />)}
                  </Pie>
                  <Tooltip formatter={v => handlePriceWithRM(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ borderRadius: '14px' }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Recent Commission Records</Typography>
          {recent.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Sales #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">kWp</TableCell>
                    <TableCell align="right">Comm</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map(r => {
                    const cd = r.commissionProgressDto;
                    const sd = r.salesDetailsForCommissionProgress;
                    const cp = cd?.commPay || 0;
                    const tp = totalPaidOf(cd);
                    const out = Math.max(0, cp - tp);
                    return (
                      <TableRow key={r.commProgressId} hover
                        onClick={() => navigate(`/accounting/commission/${r.commProgressId}`)}
                        sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                        <TableCell>{convertDateTimeToDateString(sd?.depositOn)}</TableCell>
                        <TableCell>{sd?.salesId}</TableCell>
                        <TableCell>{sd?.customerName}</TableCell>
                        <TableCell>
                          <Chip size="small" label={cd?.commPayType}
                            sx={{ backgroundColor: TYPE_COLORS[cd?.commPayType] || '#94A3B8', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell align="right">{sd?.energy ? `${sd.energy} kWp` : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{handlePriceWithRM(cp)}</TableCell>
                        <TableCell align="right" sx={{ color: '#10B981' }}>{handlePriceWithRM(tp)}</TableCell>
                        <TableCell align="right" sx={{ color: out > 0 ? '#F59E0B' : '#94A3B8' }}>{handlePriceWithRM(out)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No records</Typography>}
        </CardContent>
      </Card>
    </div>
  );
}
